import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { COACHES } from '../constants';

const STEP = 360 / COACHES.length;
const THINKING_MS = 860;
const NUMBER_PATTERN =
  /(\d[\d,.]*\+?(?:\s?(?:kg|L|minutes?|hours'?|steps|pm|am|nights?))?)/g;

type Props = {
  activeIndex: number;
  onSelect: (index: number) => void;
  onHoverChange: (isHovered: boolean) => void;
  className?: string;
};

// Bolds the numbers and keeps the last word off a line of its own.
const renderInsight = (text: string) =>
  text
    .replace(/ (\S+)$/, ' $1')
    .split(NUMBER_PATTERN)
    .map((part, index) => (index % 2 ? <b key={part}>{part}</b> : part));

const CoachRing = ({
  activeIndex,
  onSelect,
  onHoverChange,
  className,
}: Props) => {
  const [angle, setAngle] = useState(0);
  // Insight lags behind activeIndex while the "thinking" dots show.
  const [shownIndex, setShownIndex] = useState(activeIndex);
  const prevIndex = useRef(activeIndex);

  useEffect(() => {
    // Turn the shortest way round so the active coach lands on top.
    let delta =
      (activeIndex - prevIndex.current + COACHES.length) % COACHES.length;

    if (delta > COACHES.length / 2) {
      delta -= COACHES.length;
    }

    const turn = delta * STEP;

    prevIndex.current = activeIndex;
    setAngle((prev) => prev - turn);

    const timer = setTimeout(() => setShownIndex(activeIndex), THINKING_MS);

    return () => clearTimeout(timer);
  }, [activeIndex]);

  const isThinking = shownIndex !== activeIndex;

  return (
    <Ring className={className}>
      <RingLine />
      <Orbit style={{ transform: `rotate(${angle}deg)` }}>
        {COACHES.map((coach, index) => {
          const position = index * STEP;

          return (
            <Avatar
              key={coach.name}
              type="button"
              aria-label={`Show ${coach.name}'s insight`}
              aria-pressed={index === activeIndex}
              $active={index === activeIndex}
              onClick={() => onSelect(index)}
              style={{
                transform: `rotate(${position}deg) translateY(calc(-1 * var(--radius)))`,
              }}
            >
              {/* Counter-rotates so faces stay upright while the orbit turns. */}
              <AvatarInner
                $active={index === activeIndex}
                style={{ transform: `rotate(${-(position + angle)}deg)` }}
              >
                <img
                  src={coach.image}
                  alt={coach.name}
                  width={66}
                  height={66}
                />
              </AvatarInner>
            </Avatar>
          );
        })}
      </Orbit>
      {/* Mouse only: touch fires enter on tap but no leave, which would pause forever. */}
      <Card
        onPointerEnter={(e) => onHoverChange(e.pointerType === 'mouse')}
        onPointerLeave={() => onHoverChange(false)}
      >
        <CardPlasma />
        <CardWash />
        <CardBody>
          <Slot aria-live="polite">
            {isThinking ? (
              <Dots>
                <Dot />
                <Dot />
                <Dot />
              </Dots>
            ) : (
              <Insight key={shownIndex}>
                <Quote>&ldquo;</Quote>
                <InsightText>
                  {renderInsight(COACHES[shownIndex].insight)}
                </InsightText>
                <Quote $close>&rdquo;</Quote>
              </Insight>
            )}
          </Slot>
        </CardBody>
      </Card>
    </Ring>
  );
};

const ORBIT_TRANSITION = 'transform 0.65s cubic-bezier(0.45, 0, 0.25, 1)';
const NAVY = '#2E3A57';
const RING_BLUE = '#9DC3D3';
const PINK_GLOW = 'rgb(222 143 160 / 55%)';
const PLASMA_BLUE = '#B7D8E4';
const PLASMA_PINK = '#F3C6D2';
const PLASMA_DEEP_PINK = '#E6A3B6';
const PULSE_DELAY_S = 0.6;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
`;

const pulse = keyframes`
  0% {
    opacity: 1;
    box-shadow: 0 0 0 0 ${PINK_GLOW};
  }

  100% {
    opacity: 1;
    box-shadow: 0 0 0 24px rgb(222 143 160 / 0%);
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.55;
  }

  40% {
    transform: translateY(-5px);
    opacity: 1;
  }
`;

const Ring = styled.div`
  --radius: 200px;
  --avatar: 66px;
  --card-inset: 56px;

  position: relative;
  width: calc(var(--radius) * 2);
  height: calc(var(--radius) * 2);
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down('md')} {
    --radius: 163px;
    --avatar: 54px;
    --card-inset: 54px;
  }
`;

const RingLine = styled.div`
  position: absolute;
  inset: 1px;
  border-radius: 50%;
  border: 2px solid ${RING_BLUE};
  background: rgb(255 255 255 / 20%);
  pointer-events: none;
`;

const Orbit = styled.div`
  position: absolute;
  inset: 0;
  transition: ${ORBIT_TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Avatar = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--avatar);
  height: var(--avatar);
  margin: calc(var(--avatar) / -2) 0 0 calc(var(--avatar) / -2);
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  /* Pulse ring; lives here because AvatarInner clips its overflow. */
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      &::after {
        /* Delay lets the orbit finish turning before the coach "lands". */
        animation: ${pulse} 1s ease-out ${PULSE_DELAY_S}s;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const AvatarInner = styled.div<{ $active: boolean }>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.white};
  box-shadow:
    0 0 0 2px
      ${({ $active, theme }) =>
        $active ? theme.colors.secondaryV1 : theme.colors.lightBlue},
    0 8px 20px rgb(70 83 111 / 24%);
  transition:
    ${ORBIT_TRANSITION},
    box-shadow 0.25s ease;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s ease;
  }

  &:hover img {
    transform: scale(1.08);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Card = styled.div`
  position: absolute;
  inset: var(--card-inset);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.secondaryV1};
  box-shadow:
    0 22px 48px rgb(70 83 111 / 16%),
    0 4px 10px rgb(70 83 111 / 6%);
  text-align: center;
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 0 20px;
  }
`;

// Soft colour blobs on two layers turning in opposite directions; the card clips them.
const CardPlasma = styled.div`
  position: absolute;
  inset: 0;
  background: ${PLASMA_BLUE};

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: -50%;
  }

  &::before {
    background: radial-gradient(
        circle at 30% 30%,
        ${PLASMA_PINK} 0,
        transparent 40%
      ),
      radial-gradient(circle at 70% 70%, ${PLASMA_DEEP_PINK} 0, transparent 35%);
    animation: ${spin} 14s linear infinite;
  }

  &::after {
    background: radial-gradient(circle at 70% 30%, #fff 0, transparent 35%),
      radial-gradient(circle at 30% 70%, ${PLASMA_BLUE} 0, transparent 40%);
    animation: ${spin} 20s linear infinite reverse;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none;
    }
  }
`;

// Softens the plasma: white at the bottom and top-left highlight, inner shading.
const CardWash = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  background: linear-gradient(
      0deg,
      #fff 0%,
      rgb(255 255 255 / 85%) 22%,
      rgb(255 255 255 / 0%) 50%
    ),
    radial-gradient(
      ellipse 60% 45% at 32% 22%,
      rgb(255 255 255 / 75%) 0,
      rgb(255 255 255 / 0%) 100%
    ),
    radial-gradient(
      circle at 50% 45%,
      rgb(255 255 255 / 30%) 0,
      rgb(255 255 255 / 0%) 60%
    );
  box-shadow:
    inset 0 -26px 40px rgb(70 83 111 / 10%),
    inset 0 18px 30px rgb(255 255 255 / 90%),
    inset 0 0 0 1px rgb(255 255 255 / 60%);
`;

// Positioned so the text stacks above the plasma and wash.
const CardBody = styled.div`
  position: relative;
`;

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 160px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 150px;
  }
`;

const Insight = styled.div`
  animation: ${fadeUp} 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Quote = styled.div<{ $close?: boolean }>`
  height: 16px;
  color: ${({ theme }) => theme.colors.secondaryV1};
  font-size: 34px;
  font-weight: 700;
  line-height: 16px;
  transform: translateY(9px);

  ${({ $close }) =>
    $close &&
    css`
      margin-top: 20px;
      transform: translateY(-1px);
    `}
`;

const InsightText = styled.p`
  max-height: calc(1.7em * 4);
  margin: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.7;

  b {
    color: ${NAVY};
    font-weight: 700;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-height: calc(1.7em * 5);
    font-size: 13px;
  }
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 16px;
`;

const Dot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.secondaryV1};
  animation: ${bounce} 0.9s ease-in-out infinite;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default CoachRing;
